import { useStoreSetting } from '@/hooks/use-store-setting';
import { Store, Phone, Mail, Globe, MapPin } from 'lucide-react';

interface PublicStoreHeaderProps {
  /** Renders a slim single-row version suitable for a sticky top bar */
  compact?: boolean;
}

export function PublicStoreHeader({ compact = false }: PublicStoreHeaderProps) {
  const { setting, isLoading } = useStoreSetting();

  if (isLoading) {
    if (compact) {
      return (
        <div className="flex items-center gap-3 py-2.5">
          <div className="w-8 h-8 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-4 w-32 bg-slate-100 rounded animate-pulse" />
        </div>
      );
    }
    return null;
  }

  if (!setting) return null;

  /* ── Compact: single-row sticky bar ── */
  if (compact) {
    return (
      <div className="flex items-center gap-3 py-2.5">
        {/* Logo */}
        <div className="w-8 h-8 rounded-lg border border-slate-100 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
          {setting.logo ? (
            <img src={setting.logo} alt={setting.name} className="w-full h-full object-cover" />
          ) : (
            <Store className="w-4 h-4 text-slate-400" />
          )}
        </div>

        {/* Name + primary contact */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-bold text-sm text-slate-900 truncate">
            {setting.name || 'Store'}
          </span>
          {setting.phone && (
            <>
              <span className="text-slate-300 text-xs">·</span>
              <a
                href={`tel:${setting.phone}`}
                className="text-xs text-slate-500 hover:text-blue-600 transition-colors truncate hidden sm:block"
              >
                {setting.phone}
              </a>
            </>
          )}
          {(setting.addressLine1 || setting.addressLine2) && (
            <>
              <span className="text-slate-300 text-xs hidden md:block">·</span>
              <span className="text-xs text-slate-400 truncate hidden md:block">
                {[setting.addressLine1, setting.addressLine2].filter(Boolean).join(', ')}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ── Full: page hero header (original, cleaned up) ── */
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-5 py-5">
      <div className="flex flex-col items-center text-center sm:flex-row sm:text-left gap-4">
        {/* Logo */}
        <div className="flex-shrink-0">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-center overflow-hidden">
            {setting.logo ? (
              <img src={setting.logo} alt={setting.name} className="w-full h-full object-cover" />
            ) : (
              <Store className="w-7 h-7 text-slate-300" />
            )}
          </div>
        </div>

        {/* Info */}
        <div className="space-y-1.5 flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight leading-tight">
            {setting.name || 'Store Name'}
          </h1>

          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1.5 text-sm text-slate-600">
            {setting.phone && (
              <a
                href={`tel:${setting.phone}`}
                className="flex items-center gap-1.5 hover:text-blue-600 transition-colors"
              >
                <div className="p-1 rounded-full bg-blue-50 text-blue-500">
                  <Phone className="w-3 h-3" />
                </div>
                <span className="font-medium">{setting.phone}</span>
              </a>
            )}
            {setting.email && (
              <a
                href={`mailto:${setting.email}`}
                className="flex items-center gap-1.5 hover:text-blue-600 transition-colors"
              >
                <div className="p-1 rounded-full bg-orange-50 text-orange-500">
                  <Mail className="w-3 h-3" />
                </div>
                <span className="font-medium">{setting.email}</span>
              </a>
            )}
            {setting.website && (
              <a
                href={setting.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 hover:text-blue-600 transition-colors"
              >
                <div className="p-1 rounded-full bg-green-50 text-green-500">
                  <Globe className="w-3 h-3" />
                </div>
                <span className="font-medium">{setting.website}</span>
              </a>
            )}
          </div>

          {(setting.addressLine1 || setting.addressLine2) && (
            <div className="flex items-center justify-center sm:justify-start gap-1.5 text-xs text-slate-400">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">
                {[setting.addressLine1, setting.addressLine2].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}